/**
 * CDN Configuration for Image URLs
 * 
 * Handles URL transformations for images stored in Wasabi S3
 * and served through Cloudflare CDN
 */

// CDN is always enabled in production builds
export const CDN_CONFIG = {
  enabled: true,
  baseUrl: 'https://img.spomen.mk',
  bucketName: 'spomenmk',
  imageResizingEnabled: false // Set to true after enabling Cloudflare Image Resizing
};

// The demo images were optimized in place. Version their CDN URLs so
// Cloudflare does not keep serving the previous multi-megabyte objects.
const DEMO_ASSET_VERSION = '20260803-optimized';

const addDemoAssetVersion = (url) => {
  if (url?.includes('/spomenmk/demo/') && !url.includes('?')) {
    return `${url}?v=${DEMO_ASSET_VERSION}`;
  }
  return url;
};

/**
 * Transforms a Wasabi S3 URL to use Cloudflare CDN
 * 
 * @param {string} url - Original image URL (from backend API)
 * @returns {string} - CDN URL or original URL if transformation not needed
 * 
 * Examples:
 * Input:  https://s3.eu-south-1.wasabisys.com/spomenmk/images/abc/profile.jpg
 * Output: https://img.spomen.mk/spomenmk/images/abc/profile.jpg
 * 
 * Input:  images/abc/profile.jpg (relative path)
 * Output: https://img.spomen.mk/spomenmk/images/abc/profile.jpg
 */
export const getCdnUrl = (url) => {
  if (!url) return url;

  // Already a CDN URL, return as-is
  if (url.startsWith(CDN_CONFIG.baseUrl)) {
    return addDemoAssetVersion(url);
  }

  // Handle full Wasabi URLs
  const wasabiPattern = /https?:\/\/s3\.[a-z0-9-]+\.wasabisys\.com\/spomenmk\/(.*)/;
  const match = url.match(wasabiPattern);
  
  if (match) {
    // Extract path after bucket name and construct CDN URL
    const imagePath = match[1];
    return addDemoAssetVersion(`${CDN_CONFIG.baseUrl}/spomenmk/${imagePath}`);
  }

  // Handle relative paths (images/...)
  if (url.startsWith('images/')) {
    return addDemoAssetVersion(`${CDN_CONFIG.baseUrl}/spomenmk/${url}`);
  }

  // Handle paths with bucket name (spomenmk/images/...)
  if (url.startsWith('spomenmk/')) {
    return addDemoAssetVersion(`${CDN_CONFIG.baseUrl}/${url}`);
  }

  // Return original URL if no transformation is needed
  console.warn('Could not transform URL to CDN:', url);
  return url;
};

/**
 * Adds image optimization parameters to CDN URLs
 * 
 * @param {string} url - Image URL
 * @param {object} options - Optimization options
 * @param {number} options.width - Desired width in pixels
 * @param {number} options.quality - JPEG quality (1-100)
 * @param {string} options.format - Desired format (webp, jpeg, png)
 * @returns {string} - URL with optimization parameters
 * 
 * Note: Requires Cloudflare Image Resizing ($10/month)
 * Enable at: Cloudflare Dashboard > Speed > Optimization > Image Resizing
 */
export const getOptimizedCdnUrl = (url, options = {}) => {
  const cdnUrl = getCdnUrl(url);
  
  // If Image Resizing is not enabled, return regular CDN URL
  if (!CDN_CONFIG.imageResizingEnabled) {
    return cdnUrl;
  }

  const { width, quality = 85, format = 'auto' } = options;
  
  // Cloudflare Image Resizing URL format
  // https://developers.cloudflare.com/images/url-format
  const params = new URLSearchParams();
  if (width) params.set('width', width);
  if (quality) params.set('quality', quality);
  if (format) params.set('format', format);

  return `${CDN_CONFIG.baseUrl}/cdn-cgi/image/${params.toString()}/${cdnUrl.replace(CDN_CONFIG.baseUrl + '/', '')}`;
};

/**
 * Preload critical images for better performance
 * 
 * @param {string} url - Image URL to preload
 */
export const preloadImage = (url) => {
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'image';
  link.href = getCdnUrl(url);
  document.head.appendChild(link);
};

/**
 * Generate srcset for responsive images
 * 
 * @param {string} url - Base image URL
 * @param {number[]} widths - Array of widths for srcset
 * @returns {string} - srcset string
 */
export const generateSrcSet = (url, widths = [400, 800, 1200, 1600]) => {
  if (!CDN_CONFIG.imageResizingEnabled) {
    return getCdnUrl(url);
  }

  return widths
    .map(width => `${getOptimizedCdnUrl(url, { width })} ${width}w`)
    .join(', ');
};

// Export for testing/debugging
export const __testing = {
  CDN_CONFIG,
  getCdnUrl,
  getOptimizedCdnUrl
};
